import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@carepass/api-client";
import { memberRouter } from "../../app/runtime";
import { authApi, terminationResultApi } from "../shared/api";
import { IdentityVerificationForm } from "../shared/IdentityVerificationForm";
import { Alert, Button, LoadingState, Surface, navigate } from "../shared/ui";
import { TerminationResultView, TerminationScreen } from "./TerminationView";
import { clearTerminationResultSession, readTerminationResultSession, saveTerminationResultSession } from "./termination-result-session";

export function TerminationResultPage() {
  const queryClient = useQueryClient();
  const [session, setSession] = useState(readTerminationResultSession);
  const [error, setError] = useState<string>();
  const [issuing, setIssuing] = useState(false);
  const issuedSession = useRef<{ token: string; promise: Promise<void> } | undefined>(undefined);
  const resumed = useRef(false);
  const params = new URLSearchParams(memberRouter.getSnapshot().location.search);
  const verificationToken = params.get("verificationToken") ?? undefined;
  const result = useQuery({ queryKey: ["termination-result", session?.expiresAt], queryFn: () => terminationResultApi.current(session!.accessToken), enabled: Boolean(session), retry: false,
    refetchInterval: query => query.state.data?.automaticCancellation && ["REQUESTED", "PROCESSING"].includes(query.state.data.status) && !query.state.error ? 3000 : false,
  });

  const expire = useCallback(() => {
    clearTerminationResultSession();
    queryClient.removeQueries({ queryKey: ["termination-result"] });
    setSession(undefined);
    setError("결과 조회 시간이 만료되었습니다. 본인확인 후 다시 조회해 주세요.");
  }, [queryClient]);

  useEffect(() => {
    if (!session) return;
    const remaining = Date.parse(session.expiresAt) - Date.now();
    if (remaining <= 0) { expire(); return; }
    const timer = window.setTimeout(expire, remaining);
    return () => window.clearTimeout(timer);
  }, [session, expire]);
  useEffect(() => {
    if (result.error instanceof ApiError && (result.error.kind === "unauthenticated" || result.error.code === "TERMINATION_RESULT_SESSION_INVALID")) expire();
  }, [result.error, expire]);

  const recover = useCallback((token: string): Promise<void> => {
    if (issuedSession.current?.token === token) return issuedSession.current.promise;
    setIssuing(true); setError(undefined);
    const promise = terminationResultApi.issueSession(token).then(value => {
      if (!saveTerminationResultSession(value.accessToken, value.expiresAt)) throw new Error("결과 조회 권한을 확인하지 못했습니다. 다시 본인확인해 주세요.");
      queryClient.removeQueries({ queryKey: ["termination-result"] });
      setSession(readTerminationResultSession());
      navigate("/termination/result", true);
    }).catch(cause => {
      issuedSession.current = undefined;
      setError(cause instanceof Error ? cause.message : "조회 권한을 확인하지 못했습니다. 다시 시도해 주세요.");
      throw cause;
    }).finally(() => setIssuing(false));
    issuedSession.current = { token, promise };
    return promise;
  }, [queryClient]);
  useEffect(() => {
    if (resumed.current || session || params.get("verified") !== "1" || !verificationToken) return;
    resumed.current = true;
    void recover(verificationToken).catch(() => undefined);
  }, [verificationToken, session, recover]);

  return <TerminationScreen onClose={() => navigate("/")} pending={issuing}>
    {session ? <div className="termination-stack">
      {result.error && <Alert persistent variant="danger" title="처리 상태를 확인하지 못했습니다" action={<Button variant="quiet" onClick={() => void result.refetch()}>다시 조회</Button>}>{result.error.message}</Alert>}
      {result.data ? <TerminationResultView refund={result.data} refreshing={result.isFetching} onRefresh={() => void result.refetch()}/>
        : !result.isError && <div className="termination-loading"><LoadingState label="취소·환불 상태를 불러오는 중입니다."/></div>}
      <p className="termination-help">이 조회 권한은 15분 동안 유효하며, 일반 회원 서비스에는 사용할 수 없습니다.</p>
    </div> : <div className="termination-stack">
      <Alert persistent variant="information" className="termination-condition termination-condition--mint" title="해지 결과를 확인해 주세요">해지로 회원 로그인이 종료되었습니다. 본인확인 후 취소·환불 처리 상태만 조회할 수 있습니다.</Alert>
      {error && <Alert persistent variant="warning" title="본인확인이 필요합니다">{error}</Alert>}
      <Surface className="termination-card" title="휴대폰 본인인증">
        <IdentityVerificationForm
          storageKey="carepass.identity.TERMINATION_RESULT"
          initialVerificationToken={verificationToken}
          client={{ request: input => authApi.requestIdentity({ ...input, purpose: "TERMINATION_RESULT" }), status: authApi.identityStatus, confirm: authApi.confirmIdentity, resend: authApi.resendIdentity, complete: authApi.completeIdentity }}
          onVerified={recover}/>
      </Surface>
    </div>}
  </TerminationScreen>;
}
